import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

interface PartnershipMatch {
  id: string;
  date: string;
  location: string | null;
  result: 'win' | 'loss' | 'draw';
  /** @deprecated Use result instead */
  won: boolean;
  sets: Array<{ team1: number; team2: number }>;
  opponents: Array<{
    id: string;
    name: string;
    avatarUrl: string | null;
  }>;
}

interface PartnershipStats {
  partnerId: string;
  partnerName: string;
  partnerAvatarUrl: string | null;
  totalMatches: number;
  wins: number;
  losses: number;
  draws: number;
  winRate: number;
  matches: PartnershipMatch[];
  currentStreak: number;
  streakType: 'win' | 'loss' | 'none';
  bestStreak: number;
  avgSetsWon: number;
  avgSetsLost: number;
}

function computeStreaks(matches: PartnershipMatch[]) {
  let currentStreak = 0;
  let streakType: 'win' | 'loss' | 'none' = 'none';
  let bestStreak = 0;
  let tempStreak = 0;
  let lastResult: boolean | null = null;

  for (const match of matches) {
    const matchWon = match.result === 'win';
    if (lastResult === null) {
      lastResult = matchWon;
      tempStreak = 1;
      currentStreak = 1;
      streakType = matchWon ? 'win' : 'loss';
    } else if (matchWon === lastResult) {
      tempStreak++;
      currentStreak = tempStreak;
    } else {
      if (lastResult && tempStreak > bestStreak) bestStreak = tempStreak;
      tempStreak = 1;
      lastResult = matchWon;
      currentStreak = 1;
      streakType = matchWon ? 'win' : 'loss';
    }
  }
  if (lastResult && tempStreak > bestStreak) bestStreak = tempStreak;
  return { currentStreak, streakType, bestStreak };
}

export const usePartnershipHistory = (partnerId: string | null, profileUserId?: string) => {
  const { user } = useAuth();
  const effectiveUserId = profileUserId ?? user?.id;

  const { data, isLoading, error, refetch } = useQuery({
    queryKey: ['partnership-history', effectiveUserId, partnerId],
    queryFn: async () => {
      const { data, error: rpcError } = await supabase.rpc('get_partnership_history', {
        p_user_id: effectiveUserId!,
        p_partner_id: partnerId!,
        p_limit: 20,
      });

      if (rpcError) throw rpcError;

      const raw = data as any;
      const matches: PartnershipMatch[] = (raw?.matches || []).map((m: any) => ({
        ...m,
        sets: Array.isArray(m.sets) ? m.sets.map((s: any) => ({ team1: s.team1 || 0, team2: s.team2 || 0 })) : [],
      }));

      let totalSetsWon = 0;
      let totalSetsLost = 0;
      matches.forEach(m => {
        m.sets.forEach((s: { team1: number; team2: number }) => {
          if (m.result === 'win') { totalSetsWon += Math.max(s.team1, s.team2); totalSetsLost += Math.min(s.team1, s.team2); }
          else if (m.result === 'loss') { totalSetsWon += Math.min(s.team1, s.team2); totalSetsLost += Math.max(s.team1, s.team2); }
          else { totalSetsWon += s.team1; totalSetsLost += s.team2; }
        });
      });

      const matchCount = matches.length || 1;
      const { currentStreak, streakType, bestStreak } = computeStreaks(matches);
      const totalMatches = Number(raw?.totalMatches || 0);

      return {
        partnerId: partnerId!,
        partnerName: raw?.partnerName || 'Unknown',
        partnerAvatarUrl: raw?.partnerAvatarUrl || null,
        totalMatches,
        wins: Number(raw?.wins || 0),
        losses: Number(raw?.losses || 0),
        draws: Number(raw?.draws || 0),
        winRate: Number(raw?.winRate || 0),
        matches,
        currentStreak,
        streakType: totalMatches === 0 ? 'none' : streakType,
        bestStreak,
        avgSetsWon: Math.round((totalSetsWon / matchCount) * 10) / 10,
        avgSetsLost: Math.round((totalSetsLost / matchCount) * 10) / 10,
      } as PartnershipStats;
    },
    enabled: !!effectiveUserId && !!partnerId,
    staleTime: 5 * 60 * 1000,
  });

  return {
    stats: data ?? null,
    loading: isLoading,
    error: error instanceof Error ? error.message : null,
    refetch,
  };
};
